import { Injectable } from '@angular/core'
import { isNullOrUndefined } from '@gofive/angular-common'
import { map } from 'rxjs/operators'
import { DataTableModel } from 'src/domain/models/datatable.model'
import { InvoiceRepository } from 'src/domain/repositories/invoice.repository'
import { DefaultResultData, DefaultResultDataSource, GetFilterWithCountTypeEntity } from '../entities/base.entity'
import {
	InvoiceClearCacheResponseEntity,
	InvoiceDataTableTypeEntity,
	InvoiceLogsTypeEntity,
	InvoicePDFTypeEntity,
	InvoiceTypeEntity,
} from '../entities/invoice.entity'
import { GetFilterWithCountMapper } from '../mappers/base/get-filter-with-count.mapper'
import { InvoiceDataTableMapper } from '../mappers/invoice/invoice-data-table.mapper'
import { InvoiceLogsMapper } from '../mappers/invoice/invoice-logs.mapper'
import { InvoicePDFMapper } from '../mappers/invoice/invoice-pdf.mapper'
import { InvoiceMapper } from '../mappers/invoice/invoice.mapper'

@Injectable({
	providedIn: 'root',
})
export class InvoiceImplementationRepository extends InvoiceRepository {
	invoiceDataTableMapper = new InvoiceDataTableMapper()
	getFilterWithCountMapper = new GetFilterWithCountMapper()
	invoiceLogsMapper = new InvoiceLogsMapper()
	invoiceMapper = new InvoiceMapper()
	invoicePdfMapper = new InvoicePDFMapper()

	GetGeneratePDF(params: { id: number }) {
		return this.get<DefaultResultData<InvoicePDFTypeEntity>>(`api/v2/Invoices/${params.id}/GeneratePDF?type=1`).pipe(
			map((res) => {
				return this.invoicePdfMapper.mapFrom(res.data)
			}),
		)
	}

	GetInvoicePayment(params: { id: number }) {
		return this.get<DefaultResultData<InvoiceTypeEntity>>('api/Invoices/' + params.id + '/PaymentTransactions').pipe(
			map((res) => {
				res?.data?.paymentTransactions
					.filter((f) => f.attachments != null)
					.forEach((a) => {
						a.attachments = a.attachments.map((s) => {
							if (s.url) {
								s.data = s.url
								delete s.url
							}
							return s
						})
					})
				return this.invoiceMapper.mapFrom(res.data)
			}),
		)
	}

	EditInvoice(params: { invoiceId: number; addressId: number }) {
		return this.post<DefaultResultData<boolean>>(`api/Invoices/updateInvoice/${params.invoiceId}/${params.addressId}`)
	}

	GetInvoiceBySubscriptions(params: { id: number }) {
		return this.get<DefaultResultData<InvoiceTypeEntity[]>>('api/Invoices/' + params.id + '/Subscriptions').pipe(
			map((response) => {
				return response.data.map((item) => {
					return this.invoiceMapper.mapFrom(item)
				})
			}),
		)
	}

	ReSendInvoice(params: { id: number }) {
		return this.post<any>('api/Invoices/reSendInvoicePending?invoiceId=' + params.id)
	}

	ResyncD365(params: { id: number }) {
		return this.post<any>('api/Invoices/resync-d365/' + params.id)
	}

	RecreateDescriptionJson(params: { id: number }) {
		return this.put<DefaultResultData<InvoiceTypeEntity>>(
			`api/Invoices/${params.id}/recreate-description-json`,
			{},
		).pipe(
			map((response: DefaultResultData<InvoiceTypeEntity>) => {
				return this.invoiceMapper.mapFrom(response.data)
			}),
		)
	}

	GetLogsByInvoiceId(params: { id: number }) {
		return this.get<DefaultResultData<InvoiceLogsTypeEntity[]>>('api/Invoices/' + params.id + '/D365-error-log').pipe(
			map((response) => {
				return response.data.map((item) => {
					return this.invoiceLogsMapper.mapFrom(item)
				})
			}),
		)
	}

	ClearCacheInvoice(params: { id: number }) {
		return this.delete<DefaultResultData<InvoiceClearCacheResponseEntity>>('api/Invoices/clear-cache/' + params.id)
	}

	GetDataTableList(params: {
		model: DataTableModel
		preCondition: Object
		param?: Array<string>
		search?: string
		filter?: Object
	}) {
		let whereCause: string
		if (params.filter && Object.values(params.filter).filter((x) => !isNullOrUndefined(x)).length > 0) {
			whereCause = this.createWhereCause(params.preCondition, params.param, params.search, params.filter)
		} else if (!isNullOrUndefined(params.search)) {
			whereCause = this.createWhereCause(params.preCondition, params.param, params.search)
		} else {
			whereCause = this.createWhereCause(params.preCondition)
		}
		params.model.whereCause = whereCause
		if (isNullOrUndefined(params.model.orderBy)) {
			params.model.orderBy = 'InvoiceId'
		}
		return this.post<DefaultResultDataSource<InvoiceDataTableTypeEntity[]>>(
			'api/Invoices/DataTableList',
			params.model,
		).pipe(
			map((response) => {
				return response.dataSource.map((item) => {
					return this.invoiceDataTableMapper.mapFrom(item)
				})
			}),
		)
	}

	GetFilterWithCount(params: {
		model: DataTableModel
		preCondition: Object
		param?: Array<string>
		search?: string
		filter?: Object
	}) {
		let whereCause: string
		if (params.filter && Object.values(params.filter).filter((x) => !isNullOrUndefined(x)).length > 0) {
			whereCause = this.createWhereCause(params.preCondition, params.param, params.search, params.filter)
		} else if (!isNullOrUndefined(params.search)) {
			whereCause = this.createWhereCause(params.preCondition, params.param, params.search)
		} else {
			whereCause = this.createWhereCause(params.preCondition)
		}
		params.model.whereCause = whereCause
		if (isNullOrUndefined(params.model.orderBy)) {
			params.model.orderBy = 'InvoiceId'
		}
		return this.post<DefaultResultData<GetFilterWithCountTypeEntity[]>>(
			'api/Invoices/GetFilterWithCount',
			params.model,
		).pipe(
			map((response) => {
				return response.data.map((item) => {
					return this.getFilterWithCountMapper.mapFrom(item)
				})
			}),
		)
	}
}
